import { Injectable, computed, inject, signal, DestroyRef, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ConfigStore } from './config.store';
import { PermissionStore } from './permission.store';
import { AuthStore } from './auth.store';
import { SharedAuthStateService } from './shared-auth-state.service';
import { AuthService } from '../core/services/auth.service';
import { NotificationService } from '../core/services/notification.service';
import { CrossTabAuthService } from '../core/services/cross-tab-auth.service';
import { LoggerService } from '../core/services/logger.service';
import { AppStorageService } from '../core/services/app-storage.service';
import { TenantApiService } from '../core/api/services/tenant-api.service';
import {
  Tenant,
  SwitchTenantRequest,
  SwitchTenantResponse,
  TenantSettings,
  TenantFilter,
  TenantStatus,
  TenantType,
  matchesTenantFilter,
  calculateTenantStats,
  TENANT_STATUS_INFO,
  TENANT_TYPE_INFO
} from '../core/models/tenant.model';
import { firstValueFrom } from 'rxjs';

/**
 * TenantStore - Multi-tenant context management for the admin interface.
 *
 * ## ARCH-002: Circular Dependency Resolution
 *
 * This store previously had a direct dependency on AuthStore which created a potential
 * circular dependency risk (TenantStore -> AuthStore -> TenantStore if AuthStore ever
 * needed tenant info). This has been resolved by:
 *
 * 1. **SharedAuthStateService**: A minimal, dependency-free service that holds shared
 *    auth state (isAuthenticated, currentUserId, currentTenantId). AuthService writes
 *    to it, TenantStore reads from it.
 *
 * 2. **Data Flow**:
 *    ```
 *    AuthService -> SharedAuthStateService <- TenantStore
 *                          ^
 *                          |
 *                      AuthStore (sync via effect)
 *    ```
 *
 * 3. **Benefits**:
 *    - No circular dependency risk
 *    - Clear ownership: AuthService owns auth state writes
 *    - TenantStore can safely check auth status without importing AuthStore
 *    - AuthStore can now safely add tenant-awareness if needed in the future
 *
 * @see SharedAuthStateService for the shared state service
 * @see https://angular.io/errors/NG0200 for circular dependency detection
 */

// GAP-FE-035: TenantStore is intentionally NOT a signalStore. See
// `stores/ARCHITECTURE.md` (Exception 1) for the rationale — auth-critical
// bootstrap order, localStorage/CrossTabAuthService coupling, and the
// `SharedAuthStateService` circular-dependency break. Adding state here:
// extend this class; do not split it into a sibling signalStore unless the
// new state is non-auth-critical.
@Injectable({
  providedIn: 'root'
})
export class TenantStore {
  private readonly tenantApi = inject(TenantApiService);
  private readonly router = inject(Router);
  private readonly configStore = inject(ConfigStore);
  private readonly sharedAuthState = inject(SharedAuthStateService);
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);
  private readonly crossTabAuth = inject(CrossTabAuthService);
  private readonly logger = inject(LoggerService);
  private readonly appStorage = inject(AppStorageService);
  private readonly destroyRef = inject(DestroyRef);
  // GAP-023: resolve PermissionStore lazily (via Injector, not an eager field)
  // so we keep TenantStore's ARCH-002 decoupling and avoid any construction-order
  // (NG0200) risk through the PermissionStore -> PermissionWebSocketService ->
  // AuthStore chain. Only touched when a tenant switch actually succeeds.
  private readonly injector = inject(Injector);

  // State
  private readonly currentTenantSignal = signal<Tenant | null>(null);

  /**
   * CRITICAL: Cache for recent tenants data to avoid localStorage reads on every computed signal access.
   * Synced from localStorage in constructor and after recordTenantAccess().
   */
  private readonly recentTenantsDataSignal = signal<Record<string, string>>({});
  private readonly availableTenantsSignal = signal<Tenant[]>([]);
  private readonly isLoadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  private readonly selectedTenantIdSignal = signal<string | null>(null);

  // COLD-LOAD RACE FIX (HAR 2026-07-01): single-flight handles. On a cold load
  // the bootstrap establishTenantContext() and tenantGuard both drive tenant
  // establishment concurrently; without sharing the in-flight load/switch the
  // guard's switchTenant() hit the isLoading early-return, got `false`, saw
  // currentTenant()===null, and spuriously redirected to /auth/login. Sharing
  // one in-flight promise per operation collapses the race.
  private loadTenantsInFlight: Promise<void> | null = null;
  private switchInFlight: { tenantId: string; promise: Promise<boolean> } | null = null;

  /**
   * DEV-190: Filter state for multi-tenant dashboard
   */
  private readonly filterSignal = signal<TenantFilter>({});

  // Public readonly signals
  readonly currentTenant = computed(() => this.currentTenantSignal());
  readonly availableTenants = computed(() => this.availableTenantsSignal());
  readonly isLoading = computed(() => this.isLoadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly selectedTenantId = computed(() => this.selectedTenantIdSignal());

  /**
   * DEV-190: Current filter state
   */
  readonly filter = computed(() => this.filterSignal());

  /**
   * DEV-190: Filtered tenants based on current filter criteria
   */
  readonly filteredTenants = computed(() => {
    const tenants = this.availableTenantsSignal();
    const filter = this.filterSignal();

    // If no filter criteria, return all tenants
    if (!filter.statuses?.length && !filter.types?.length && !filter.searchTerm && !filter.hasFeature && filter.expiringWithinDays === undefined) {
      return tenants;
    }

    return tenants.filter(tenant => matchesTenantFilter(tenant, filter));
  });

  /**
   * DEV-190: Tenant statistics computed from available tenants
   */
  readonly tenantStats = computed(() => {
    const tenants = this.availableTenantsSignal();
    return calculateTenantStats(tenants);
  });

  /**
   * DEV-190: Quick count signals for dashboard cards
   */
  readonly activeTenantsCount = computed(() => this.tenantStats().byStatus.active);
  readonly suspendedTenantsCount = computed(() => this.tenantStats().byStatus.suspended);
  readonly trialTenantsCount = computed(() => this.tenantStats().byStatus.trial);
  readonly pendingApprovalCount = computed(() => this.tenantStats().pendingApproval);
  readonly expiringTrialsCount = computed(() => this.tenantStats().expiringTrials);
  readonly totalTenantsCount = computed(() => this.tenantStats().total);
  readonly newTenantsThisMonthCount = computed(() => this.tenantStats().newThisMonth);

  /**
   * DEV-190: Check if any filter is active
   */
  readonly hasActiveFilter = computed(() => {
    const filter = this.filterSignal();
    return !!(filter.statuses?.length || filter.types?.length || filter.searchTerm || filter.hasFeature || filter.expiringWithinDays !== undefined);
  });

  // Computed values
  readonly hasTenant = computed(() => this.currentTenantSignal() !== null);
  readonly hasMultipleTenants = computed(() => this.availableTenantsSignal().length > 1);
  readonly canSwitchTenant = computed(() =>
    this.hasMultipleTenants() && !this.isLoadingSignal() && this.sharedAuthState.isAuthenticated()
  );

  readonly tenantName = computed(() => this.currentTenantSignal()?.name || 'No Tenant Selected');
  readonly tenantCode = computed(() => this.currentTenantSignal()?.code || '');
  readonly tenantLogo = computed(() => this.currentTenantSignal()?.logo || 'assets/default-tenant-logo.svg');
  
  readonly tenantFeatures = computed(() => {
    const tenant = this.currentTenantSignal();
    return tenant?.features || [];
  });

  readonly hasFeature = computed(() => (feature: string): boolean => this.tenantFeatures().includes(feature));

  readonly tenantSettings = computed(() => {
    const tenant = this.currentTenantSignal();
    return tenant?.settings || {
      theme: 'light',
      language: 'en',
      timezone: 'UTC',
      dateFormat: 'YYYY-MM-DD'
    };
  });

  /**
   * DEV-29: Recent tenants sorted by lastAccessed (most recent first)
   * Returns up to 5 most recently accessed tenants
   *
   * CRITICAL: Uses cached recentTenantsDataSignal instead of reading localStorage
   * on every access to avoid performance issues in computed signals.
   */
  readonly recentTenants = computed(() => {
    const tenants = this.availableTenantsSignal();
    const recentData = this.recentTenantsDataSignal();

    // Merge recent access data with available tenants
    const tenantsWithAccess = tenants.map(tenant => ({
      ...tenant,
      lastAccessed: recentData[tenant.id] ? new Date(recentData[tenant.id]) : undefined
    }));

    // Sort by lastAccessed (most recent first), then by name for those never accessed
    return tenantsWithAccess
      .filter(t => t.lastAccessed)
      .sort((a, b) => {
        const aTime = a.lastAccessed?.getTime() || 0;
        const bTime = b.lastAccessed?.getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, 5);
  });

  constructor() {
    // Load tenant from localStorage on init
    this.loadStoredTenant();

    // Sync recent tenants cache from localStorage
    this.syncRecentTenantsFromStorage();

    // Do NOT auto-load tenants in constructor to avoid race condition
    // Tenants will be loaded explicitly after login completes
    // or when explicitly requested by calling loadAvailableTenants()

    // DEV-200: Subscribe to cross-tab tenant switch events
    this.crossTabAuth.tenantSwitch$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(message => {
        if (message.payload?.tenant) {
          this.handleCrossTabTenantSwitch(message.payload.tenant);
        }
      });
  }

  /**
   * Load available tenants for the current user from the API.
   *
   * Fetches all tenants the authenticated user has access to and populates
   * the availableTenants signal. If only one tenant is available, it will
   * be auto-selected.
   *
   * IMPORTANT: Uses AuthService.isAuthenticated() directly instead of AuthStore
   * to avoid race condition where store state may not be synced after token refresh.
   * The AuthService signal is updated immediately when token refresh completes,
   * while the AuthStore may have stale state from initial sync.
   *
   * @returns Promise that resolves when tenants are loaded
   * @throws Sets error signal on network or server errors (does not throw)
   *
   * @example
   * ```typescript
   * // After login, load available tenants
   * await tenantStore.loadAvailableTenants();
   * this.logger.debug('Available tenants', { tenants: tenantStore.availableTenants() });
   * ```
   */
  async loadAvailableTenants(): Promise<void> {
    // Single-flight (see field comment): the bootstrap establish and the guard
    // both call this on cold load. Sharing one in-flight load means the guard
    // observes the auto-selected currentTenant the load establishes, instead of
    // racing a second load/switch.
    if (this.loadTenantsInFlight) {
      return this.loadTenantsInFlight;
    }
    this.loadTenantsInFlight = this.loadAvailableTenantsInternal()
      .finally(() => { this.loadTenantsInFlight = null; });
    return this.loadTenantsInFlight;
  }

  private async loadAvailableTenantsInternal(): Promise<void> {
    // ARCH-002: Use SharedAuthStateService for auth state (breaks circular dependency)
    // SharedAuthStateService is updated by AuthService on login/logout/token refresh.
    // We also check AuthService directly as a fallback for edge cases during page refresh.
    const sharedAuthIsAuthenticated = this.sharedAuthState.isAuthenticated();
    const serviceAuthenticated = this.authService.isAuthenticated();
    const hasToken = !!this.authService.getAccessToken();

    // Accept either source as valid
    const isAuthenticated = sharedAuthIsAuthenticated || serviceAuthenticated;

    if (!isAuthenticated) {
      this.logger.debug('[TenantStore] loadAvailableTenants: Not authenticated', {
        sharedAuthIsAuthenticated,
        serviceAuthenticated,
        hasToken
      });
      this.availableTenantsSignal.set([]);
      return;
    }

    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.tenantApi.getAvailableTenants()
      );

      // API returns { tenants: [...] }, extract the array
      const tenants = response.tenants || [];
      this.availableTenantsSignal.set(tenants);

      // If no current tenant and only one available, auto-select it
      if (!this.currentTenantSignal() && tenants.length === 1) {
        // Fix race condition: Release loading lock so switchTenant can proceed
        this.isLoadingSignal.set(false);
        // Auto-select without reloading the page (we are likely in initialization/login flow)
        await this.switchTenant(tenants[0].id, undefined, { skipReload: true });
      }
    } catch (error: unknown) {
      this.logger.error('Failed to load available tenants', { error });
      this.errorSignal.set('Failed to load available tenants');
      this.notificationService.showError('Error', 'Could not load available tenants');
    } finally {
      // Ensure loading is false (if switchTenant didn't already handle it)
      this.isLoadingSignal.set(false);
    }
  }

  /**
   * F1b COLD-LOAD RACE FIX — establish the user's current tenant context
   * BEFORE the router activates, as part of the blocking app-initializer.
   *
   * THE RACE THIS KILLS: on a cold load (full reload / deep-link into a gated
   * route) the TenantStore starts empty — `loadStoredTenant()` only restores
   * `selectedTenantId` ({id,code} is all that's persisted), NOT `currentTenant`.
   * So `tenantGuard` sees `currentTenant() === null` and AUTO-SWITCHES the single
   * tenant via `switchTenant()`. That async switch (server round-trip + permission
   * reload + a route re-navigation) runs CONCURRENTLY with the guard chain, so
   * `permissionGuard` re-evaluates against the pre-switch (empty) role set and
   * redirects to /unauthorized ~0–3s after the page paints.
   *
   * By establishing `currentTenant` here — awaited inside the same
   * `provideAppInitializer` path that already awaits `initializeSession()` +
   * `loadEffectivePermissions()` — the tenant is already present by the time
   * `tenantGuard` runs on the first navigation, so the guard returns at its
   * `if (tenantStore.currentTenant()) return true;` fast-path and NEVER triggers
   * the racy auto-switch.
   *
   * IMPORTANT: this does NOT call `switchTenant()`. switchTenant() performs a
   * server-side context switch AND a route re-navigation; doing that at bootstrap
   * would re-introduce a race and a navigation flash. Cold load is a RESTORE, not
   * a SWITCH — the backend already holds this user's `current_tenant_id`, so we
   * only need to mirror it into the in-memory signal and hydrate permissions for it.
   *
   * Resolution order for the tenant to establish:
   *   1. persisted `selectedTenantId` (localStorage) matched against availableTenants
   *   2. backend `getCurrentTenant()` (authoritative `current_tenant_id`)
   *   3. the sole available tenant when exactly one exists
   * If none resolve (genuinely multi-tenant user with no prior selection), we
   * leave `currentTenant` null and let `tenantGuard` route to /tenant-selection —
   * that path is intentional, not a race.
   *
   * Fail-safe: never throws. On any error it logs and leaves `currentTenant`
   * unset so the guard chain decides (deny / tenant-selection) — no infinite
   * spinner, no thrown bootstrap failure.
   */
  async establishTenantContext(): Promise<void> {
    // Only meaningful for an authenticated principal; unauthenticated cold loads
    // fall through to authGuard → /auth/login.
    if (!this.authService.isAuthenticated()) {
      return;
    }

    // Already established (warm path / re-entrancy guard) — nothing to do.
    if (this.currentTenantSignal()) {
      return;
    }

    try {
      // 1. Load the tenants this user can access (authoritative membership list).
      if (!this.availableTenantsSignal().length) {
        await this.loadAvailableTenants();
      }

      // loadAvailableTenants() auto-selects + establishes currentTenant when the
      // user has exactly one tenant (it calls switchTenant with skipReload). If
      // that happened, currentTenant is now set and we only need to ensure
      // permissions are hydrated for it below.
      let established = this.currentTenantSignal();
      const available = this.availableTenantsSignal();

      if (!established && available.length) {
        // 2. Prefer the persisted selection (set by loadStoredTenant()).
        const persistedId = this.selectedTenantIdSignal();
        let target: Tenant | undefined = persistedId
          ? available.find(t => t.id === persistedId)
          : undefined;

        // 3. Otherwise ask the backend which tenant is current (current_tenant_id).
        if (!target) {
          try {
            const backendCurrent = await firstValueFrom(this.tenantApi.getCurrentTenant());
            if (backendCurrent) {
              target = available.find(t => t.id === backendCurrent.id) ?? backendCurrent;
            }
          } catch (error) {
            this.logger.debug(
              '[TenantStore] getCurrentTenant unavailable during cold-load establish; ' +
              'falling back to single-tenant / tenant-selection',
              { error }
            );
          }
        }

        // 4. Last resort: a lone available tenant.
        if (!target && available.length === 1) {
          target = available[0];
        }

        if (target) {
          // In-memory establish ONLY — no server switch, no route re-navigation.
          this.currentTenantSignal.set(target);
          this.selectedTenantIdSignal.set(target.id);
          this.storeTenant(target);
          this.recordTenantAccess(target.id);
          established = target;
        }
      }

      if (!established) {
        // Multi-tenant user with no prior selection — intentional: tenantGuard
        // will route to /tenant-selection. No error, no race.
        this.logger.debug(
          '[TenantStore] No tenant established at bootstrap; deferring to tenantGuard ' +
          '(tenant-selection or single-tenant auto-select)'
        );
        return;
      }

      // Hydrate roles/permissions for the established tenant BEFORE bootstrap
      // resolves, so permissionGuard/roleGuard see real data on the first
      // navigation. currentTenant is set above, so tenant.interceptor stamps the
      // correct X-Tenant-Id on these requests.
      const hydrated = await this.authService.loadEffectivePermissions();
      if (hydrated) {
        // AuthStore mirrors AuthService.currentUser via an effect() that flushes
        // ASYNCHRONOUSLY; the guard chain reads AuthStore synchronously on the
        // first navigation. Force a synchronous sync so the freshly-hydrated
        // roles/permissions are observable immediately (same pattern as the
        // switchTenant() and login() fixes). Resolved lazily to preserve
        // ARCH-002 decoupling.
        this.injector.get(AuthStore).syncWithAuthService();
      } else {
        this.logger.warn(
          '[TenantStore] Effective-permissions hydration failed during cold-load establish; ' +
          'guards may deny until next successful load (fail-safe)',
          { tenantId: established.id }
        );
      }
    } catch (error) {
      // Fail-safe: never wedge bootstrap. Leave currentTenant as-is and let the
      // guard chain decide.
      // Unlike the sibling hydration-failed WARN above (an EXPECTED, tolerated
      // outcome where permissions simply did not load), reaching this catch means
      // something in the try block THREW unexpectedly (e.g. syncWithAuthService
      // TypeError, storeTenant localStorage quota/serialization error) — a genuine
      // bug worth exactly one prod Sentry event. Log at ERROR: warn is gated out
      // before the Sentry bridge in prod (logging.level='error'), so a warn here
      // would vanish entirely. The bridge's transient-network filter still drops a
      // transient status-0 cause, so this does NOT reintroduce SENTRY-NOISE-CLUSTER
      // noise — only real logic failures surface.
      this.logger.error('[TenantStore] establishTenantContext failed (fail-safe)', { error });
    }
  }

  /**
   * Switch the current user to a different tenant context.
   *
   * Validates that the user has access to the target tenant, makes a server
   * call to switch context (which may update tokens/permissions), stores
   * the new tenant in localStorage, broadcasts the change to other tabs,
   * and reloads the current route to refresh data.
   *
   * @param tenantId - The unique tenant identifier (UUID format) to switch to
   * @param reason - Optional reason for the switch (for audit logging)
   * @returns true if switch was successful, false otherwise
   *
   * @example
   * ```typescript
   * const success = await tenantStore.switchTenant('123e4567-e89b-12d3-a456-426614174000');
   * if (success) {
   *   this.logger.info('Switched to', { tenantName: tenantStore.tenantName() });
   * }
   * ```
   */
  async switchTenant(tenantId: string, reason?: string, options?: { skipReload?: boolean }): Promise<boolean> {
    // Single-flight by target tenant (see field comment): if a switch to THIS
    // tenant is already running (e.g. the bootstrap auto-select racing the
    // guard's auto-select — both target the user's single tenant), share it so
    // the second caller gets the real result instead of a spurious `false`.
    if (this.switchInFlight && this.switchInFlight.tenantId === tenantId) {
      return this.switchInFlight.promise;
    }
    const promise = this.switchTenantInternal(tenantId, reason, options);
    this.switchInFlight = { tenantId, promise };
    return promise.finally(() => {
      if (this.switchInFlight?.promise === promise) {
        this.switchInFlight = null;
      }
    });
  }

  private async switchTenantInternal(tenantId: string, reason?: string, options?: { skipReload?: boolean }): Promise<boolean> {
    if (this.isLoadingSignal()) {
      return false;
    }

    // Check if tenant is available
    const tenant = this.availableTenantsSignal().find(t => t.id === tenantId);
    if (!tenant) {
      this.notificationService.showError('Error', 'Tenant not available');
      return false;
    }

    // Check if already current tenant
    if (this.currentTenantSignal()?.id === tenantId) {
      this.notificationService.showInfo('Info', 'Already in selected tenant');
      return true;
    }

    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const request: SwitchTenantRequest = { tenant_id: tenantId, reason };

      const response = await firstValueFrom(
        this.tenantApi.switchTenant(request)
      );

      if (response.success) {
        // Update current tenant
        this.currentTenantSignal.set(response.tenant);
        this.selectedTenantIdSignal.set(response.tenant.id);

        // Store in localStorage
        this.storeTenant(response.tenant);

        // DEV-29: Record tenant access for recent tenants feature
        this.recordTenantAccess(response.tenant.id);

        // GAP-023: The backend returns the live database-backed permission
        // snapshot for the new tenant. The previous commented-out no-op silently discarded it, so
        // on the skipReload path the displayed/checkable permission set stayed
        // stale (the reload path masked it by re-initing components). The token
        // branch was dead — the backend returns null for `token` — so it has
        // been removed entirely.
        //
        // We refresh the PermissionStore rather than shoving the raw string[]
        // into it: permission checks (hasPermission / currentUserPermissions)
        // derive from the typed roles + effectivePermissions the store loads,
        // not from a flat name list. Because currentTenant was just updated
        // above, the tenant.interceptor now stamps the new X-Tenant-Id on these
        // requests, so refresh() pulls the authoritative permission set for the
        // tenant we switched into. The guarded check keeps this a no-op only
        // when the backend genuinely returned no permissions.
        if (response.permissions) {
          const permissionStore = this.injector.get(PermissionStore);
          permissionStore.refresh();
          this.logger.debug('[TenantStore] Refreshed permissions for new tenant context', {
            tenantId: response.tenant.id,
            permissionCount: response.permissions.length
          });
        }

        // F1b RACE FIX: Re-hydrate currentUser().roles/permissions for the tenant
        // we just switched into, and AWAIT it before this method resolves /
        // before the route re-navigation below. The route guards
        // (roleGuard/permissionGuard) read authStore.hasAnyRole()/hasAnyPermission()
        // which derive from currentUser().roles/permissions. On a COLD load into a
        // gated route, tenantGuard auto-switches the single tenant, then the
        // permissionGuard re-evaluates immediately. Without this await, the guard
        // re-runs against the PRE-switch (often empty) role set and denies →
        // /unauthorized. permissionStore.refresh() above is fire-and-forget and does
        // NOT update currentUser, so it cannot close this race. currentTenant was set
        // above, so the tenant.interceptor stamps the new X-Tenant-Id and this fetch
        // returns the authoritative roles/permissions for the switched-into tenant.
        //
        // Fail-safe: loadEffectivePermissions() never throws and returns false on
        // failure (leaving roles as-is). We log the failure but still treat the
        // tenant switch as successful — the guard will deny (fail-safe) rather than
        // infinite-spin, and the user can retry.
        const hydrated = await this.authService.loadEffectivePermissions(response.tenant.id);
        if (hydrated) {
          // loadEffectivePermissions() updates AuthService.currentUser (a signal).
          // AuthStore mirrors that signal via an effect(), but effects flush
          // ASYNCHRONOUSLY — the guard chain that called us reads AuthStore's
          // synced `user` SYNCHRONOUSLY right after we resolve, before the effect
          // has run. The proven warm-login fix (auth.store.ts login()) closes the
          // same gap by patching AuthStore immediately after hydration. We do the
          // equivalent here by forcing a synchronous sync, so roleGuard/permissionGuard
          // observe the freshly-hydrated roles/permissions on the very next tick.
          // AuthStore is resolved lazily via the injector (same pattern as
          // PermissionStore above) to preserve ARCH-002 decoupling — neither
          // AuthService nor AuthStore depends on TenantStore, so there is no
          // NG0200 construction-order risk.
          const authStore = this.injector.get(AuthStore);
          authStore.syncWithAuthService();
        } else {
          this.logger.warn(
            '[TenantStore] Effective-permissions hydration failed after tenant switch; ' +
            'guards may deny until next successful load',
            { tenantId: response.tenant.id }
          );
        }

        this.notificationService.showSuccess('Success', `Switched to ${response.tenant.name}`);

        // DEV-200: Broadcast tenant switch to other tabs
        this.crossTabAuth.broadcastTenantSwitch({
          id: response.tenant.id,
          name: response.tenant.name,
          code: response.tenant.code
        });

        // MEDIUM-004: Reload current route to refresh data with new tenant context
        // Use replaceUrl instead of skipLocationChange to prevent navigation flash
        // and preserve proper browser history behavior
        
        // Fix for infinite loop on login: Don't reload if we are on login page or if skipReload is requested
        const currentUrl = this.router.url;
        const isAuthRoute = currentUrl.includes('/auth/') || currentUrl.includes('/login');
        
        if (!options?.skipReload && !isAuthRoute) {
          await this.router.navigateByUrl('/', { replaceUrl: true });
          await this.router.navigateByUrl(currentUrl, { replaceUrl: true });
        } else {
           this.logger.debug('[TenantStore] Skipping route reload', { skipReload: options?.skipReload, isAuthRoute });
        }

        return true;
      } else {
        throw new Error('Switch tenant failed');
      }
    } catch (error: unknown) {
      this.logger.error('Failed to switch tenant', { error });
      this.errorSignal.set('Failed to switch tenant');
      this.notificationService.showError('Error', 'Could not switch tenant');
      return false;
    } finally {
      this.isLoadingSignal.set(false);
    }
  }

  /**
   * Clear the current tenant context.
   *
   * Resets all tenant-related state including current tenant, available tenants,
   * selected ID, and removes stored tenant data from localStorage. Should be
   * called during logout to ensure clean state.
   *
   * @example
   * ```typescript
   * // During logout
   * authStore.logout();
   * tenantStore.clearTenant();
   * ```
   */
  clearTenant(): void {
    this.currentTenantSignal.set(null);
    this.selectedTenantIdSignal.set(null);
    this.availableTenantsSignal.set([]);
    this.errorSignal.set(null);
    this.appStorage.removeItem('currentTenant');
    this.appStorage.removeItem('selectedTenantId');
  }

  /**
   * Refresh the current tenant's data from the server.
   *
   * Fetches the latest tenant information and updates the local state.
   * Useful when tenant settings may have changed on the server.
   *
   * @returns Promise that resolves when refresh is complete
   * @throws Sets error signal on network errors (does not throw)
   *
   * @example
   * ```typescript
   * // After admin updates tenant settings
   * await tenantStore.refreshCurrentTenant();
   * this.logger.debug('Updated settings', { settings: tenantStore.tenantSettings() });
   * ```
   */
  async refreshCurrentTenant(): Promise<void> {
    const currentTenantId = this.currentTenantSignal()?.id;
    if (!currentTenantId) {
      return;
    }

    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const tenant = await firstValueFrom(
        this.tenantApi.getTenantById(currentTenantId)
      );

      this.currentTenantSignal.set(tenant);
      this.storeTenant(tenant);
    } catch (error: unknown) {
      this.logger.error('Failed to refresh tenant', { error });
      this.errorSignal.set('Failed to refresh tenant data');
    } finally {
      this.isLoadingSignal.set(false);
    }
  }

  /**
   * Check if user has access to specific tenant feature
   */
  hasAccessToFeature(feature: string): boolean {
    const tenant = this.currentTenantSignal();
    if (!tenant) {
      return false;
    }
    return tenant.features.includes(feature);
  }

  // ============================================================================
  // DEV-190: Filter Methods for Multi-Tenant Dashboard
  // ============================================================================

  /**
   * DEV-190: Set filter criteria for tenant list
   */
  setFilter(filter: TenantFilter): void {
    this.filterSignal.set(filter);
    this.logger.debug('[TenantStore] Filter updated', { filter });
  }

  /**
   * DEV-190: Update specific filter properties (merge with existing)
   */
  updateFilter(partialFilter: Partial<TenantFilter>): void {
    const currentFilter = this.filterSignal();
    this.filterSignal.set({ ...currentFilter, ...partialFilter });
    this.logger.debug('[TenantStore] Filter partially updated', { partialFilter });
  }

  /**
   * DEV-190: Clear all filter criteria
   */
  clearFilter(): void {
    this.filterSignal.set({});
    this.logger.debug('[TenantStore] Filter cleared');
  }

  /**
   * DEV-190: Filter by status(es)
   */
  filterByStatus(statuses: TenantStatus[]): void {
    this.updateFilter({ statuses });
  }

  /**
   * DEV-190: Filter by type(s)
   */
  filterByType(types: TenantType[]): void {
    this.updateFilter({ types });
  }

  /**
   * DEV-190: Filter by search term (name, code, or domain)
   */
  filterBySearchTerm(searchTerm: string): void {
    this.updateFilter({ searchTerm: searchTerm.trim() || undefined });
  }

  /**
   * DEV-190: Filter by expiring within N days
   */
  filterByExpiring(days: number): void {
    this.updateFilter({ expiringWithinDays: days });
  }

  /**
   * DEV-190: Get status metadata for display
   */
  getStatusInfo(status: TenantStatus) {
    return TENANT_STATUS_INFO[status];
  }

  /**
   * DEV-190: Get type metadata for display
   */
  getTypeInfo(type: TenantType) {
    return TENANT_TYPE_INFO[type];
  }

  /**
   * DEV-190: Get all available statuses for filter dropdown
   */
  getAllStatuses(): TenantStatus[] {
    return Object.keys(TENANT_STATUS_INFO) as TenantStatus[];
  }

  /**
   * DEV-190: Get all available types for filter dropdown
   */
  getAllTypes(): TenantType[] {
    return Object.keys(TENANT_TYPE_INFO) as TenantType[];
  }

  /**
   * Get tenant-specific configuration
   *
   * MEDIUM-006: Type-safe configuration access with proper constraint
   *
   * @param key - The settings key to retrieve (must be a valid TenantSettings key)
   * @returns The configuration value or null if not found
   *
   * @example
   * ```typescript
   * const theme = tenantStore.getTenantConfig('theme'); // string | null
   * const language = tenantStore.getTenantConfig('language'); // string | null
   * ```
   */
  getTenantConfig<K extends keyof TenantSettings>(key: K): TenantSettings[K] | null {
    const tenant = this.currentTenantSignal();
    if (!tenant || !tenant.settings) {
      return null;
    }
    return tenant.settings[key] ?? null;
  }

  /**
   * Get custom tenant configuration that may not be in TenantSettings
   *
   * Use this for extension properties that are not part of the base TenantSettings type.
   * For known settings keys, prefer getTenantConfig() for type safety.
   *
   * @warning No runtime type validation - caller is responsible for type correctness.
   * The generic type T is not validated at runtime. The returned value is cast to T
   * without verification, which may cause runtime errors if the actual value type
   * does not match T.
   *
   * @param key - The custom settings key
   * @returns The configuration value or null if not found
   */
  getCustomTenantConfig<T>(key: string): T | null {
    const tenant = this.currentTenantSignal();
    if (!tenant || !tenant.settings) {
      return null;
    }
    // Use indexed access for custom keys not in TenantSettings
    // WARNING: No runtime type validation - value is cast to T without verification
    const settings = tenant.settings as Record<string, unknown>;
    const value = settings[key];
    return value !== undefined ? (value as T) : null;
  }

  /**
   * Store tenant in localStorage.
   * SEC-002: Only persist non-PII identifiers (id, code) needed for session rehydration.
   * Full tenant data (name, contactEmail, features, settings) stays in memory only.
   */
  private storeTenant(tenant: Tenant): void {
    try {
      const minimal = { id: tenant.id, code: tenant.code };
      this.appStorage.setItem('currentTenant', JSON.stringify(minimal));
      this.appStorage.setItem('selectedTenantId', tenant.id);
    } catch (error) {
      this.logger.error('Failed to store tenant in localStorage', { error });
    }
  }

  /**
   * DEV-29: Load recent tenants access data from localStorage
   * Returns a map of tenantId -> ISO timestamp
   */
  private loadRecentTenantsData(): Record<string, string> {
    try {
      const data = this.appStorage.getItem('recentTenants');
      if (data) {
        return JSON.parse(data) as Record<string, string>;
      }
    } catch (error) {
      this.logger.error('Failed to load recent tenants data', { error });
      this.appStorage.removeItem('recentTenants');
    }
    return {};
  }

  /**
   * Sync recent tenants cache from localStorage.
   * Called in constructor and after recordTenantAccess() to keep signal in sync.
   */
  private syncRecentTenantsFromStorage(): void {
    this.recentTenantsDataSignal.set(this.loadRecentTenantsData());
  }

  /**
   * DEV-29: Record tenant access timestamp
   */
  private recordTenantAccess(tenantId: string): void {
    try {
      const recentData = this.loadRecentTenantsData();
      recentData[tenantId] = new Date().toISOString();

      // Keep only the most recent 10 entries to prevent unbounded growth
      const entries = Object.entries(recentData)
        .sort((a, b) => new Date(b[1]).getTime() - new Date(a[1]).getTime())
        .slice(0, 10);

      this.appStorage.setItem('recentTenants', JSON.stringify(Object.fromEntries(entries)));

      // Sync cache after updating localStorage
      this.syncRecentTenantsFromStorage();
    } catch (error) {
      this.logger.error('Failed to record tenant access', { error });
    }
  }

  /**
   * Load tenant from localStorage.
   * SEC-002: localStorage only stores { id, code }. We set the selectedTenantId
   * so that the tenant can be fully fetched from the API after auth completes.
   */
  private loadStoredTenant(): void {
    try {
      const storedTenant = this.appStorage.getItem('currentTenant');
      const storedTenantId = this.appStorage.getItem('selectedTenantId');

      if (storedTenant) {
        const parsed = JSON.parse(storedTenant) as { id: string; code?: string };
        // Only restore the selected tenant ID — full tenant data will be
        // populated from the API when selectTenant() or loadAvailableTenants() runs.
        this.selectedTenantIdSignal.set(storedTenantId || parsed.id);
      }
    } catch (error) {
      this.logger.error('Failed to load tenant from localStorage', { error });
      this.appStorage.removeItem('currentTenant');
      this.appStorage.removeItem('selectedTenantId');
    }
  }

  /**
   * Handle tenant switch from another tab
   * DEV-200: Cross-tab tenant synchronization
   */
  private handleCrossTabTenantSwitch(tenantInfo: { id: string; name: string; code: string }): void {
    this.logger.debug('[TenantStore] Received cross-tab tenant switch', tenantInfo);

    // Skip if same tenant
    if (this.currentTenantSignal()?.id === tenantInfo.id) {
      return;
    }

    // Find the full tenant in available tenants
    const fullTenant = this.availableTenantsSignal().find(t => t.id === tenantInfo.id);

    if (fullTenant) {
      // Update state with full tenant data
      this.currentTenantSignal.set(fullTenant);
      this.selectedTenantIdSignal.set(fullTenant.id);
      this.storeTenant(fullTenant);
      this.recordTenantAccess(fullTenant.id);

      // Notify user
      this.notificationService.showInfo(
        'Tenant Changed',
        `Another tab switched to ${fullTenant.name}`
      );

      // MEDIUM-004: Reload current route to refresh data with new tenant context
      // Use replaceUrl instead of skipLocationChange to prevent navigation flash
      const currentUrl = this.router.url;
      this.router.navigateByUrl('/', { replaceUrl: true }).then(() => {
        this.router.navigateByUrl(currentUrl, { replaceUrl: true });
      });
    } else {
      // Tenant not in available list - reload available tenants
      this.logger.warn('[TenantStore] Cross-tab tenant not in available list, reloading');
      this.loadAvailableTenants().then(() => {
        const reloadedTenant = this.availableTenantsSignal().find(t => t.id === tenantInfo.id);
        if (reloadedTenant) {
          this.currentTenantSignal.set(reloadedTenant);
          this.selectedTenantIdSignal.set(reloadedTenant.id);
          this.storeTenant(reloadedTenant);

          // Reload current route to refresh data with new tenant context
          const currentUrl = this.router.url;
          this.router.navigateByUrl('/', { replaceUrl: true }).then(() => {
            this.router.navigateByUrl(currentUrl, { replaceUrl: true });
          });
        }
      });
    }
  }
}
